import React, { useState } from "react";
import "./referForm.scss";
import BreadCrumsHeader from "../../common/BreadCrums";
import Input from "../../common/FormInputs";
import PhoneFormInput from "../../common/PhoneInput";
import { ReactComponent as CameraIcon } from "../../../Assets/Icons/cameraIcon.svg"; // (no longer used; kept only if referenced elsewhere)
import { useFormik } from "formik";
import { referSchema, reReferSchema } from "../../../utils/validationSchemas";
import { referMerchant, getFundraiserProfile, getFundraiserReferrals } from "../../../services/fundraiserService";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "../../../utils/toast";
import Loader from "../../common/Loader";
import InviteMerchantInfographic from "../../../Assets/Icons/fundraiserinvitemerchantinfographic.svg";
import InviteMerchantInfographicMobile from "../../../Assets/Icons/fundraiserinvitemerchantmobileview.svg";
import ReinvitationInfographic from "../../../Assets/Icons/re-invitationinvitemerchantinfo.svg";
import ReinvitationInfographicMobile from "../../../Assets/Icons/re-invitationmerchantmobileinfo.svg";
import EnvelopeIcon from "../../../Assets/Icons/envelopeicon.svg";
import { ReactComponent as SwoodleLogo } from "../../../Assets/Icons/SwoodleUpdatedLogo.svg";
import { Monitor, Smartphone } from "lucide-react";
import { FaLinkedinIn, FaTiktok, FaInstagram, FaFacebookF, FaYoutube } from 'react-icons/fa';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axiosInstance from "../../../utils/axios";

const ReferFormPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((state) => state?.fundraiserReducer?.loading);
  const [fundraiserName, setFundraiserName] = useState("Swoodle Fundraiser");
  const [previewMode, setPreviewMode] = useState("desktop");

  const [isReinvitation, setIsReinvitation] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailExistsAsUser, setEmailExistsAsUser] = useState(false);
  const [existingEmailType, setExistingEmailType] = useState(null);
  // Captures Quill's normalised version of the template on first render.
  // We use a ref so it never triggers re-renders.
  const quillBaselineRef = React.useRef(null);
  const isMessageDirty = React.useRef(false);

  const inferExistingAccountType = React.useCallback((source) => {
    const text = String(source || "").toLowerCase();

    if (!text) return null;
    if (text.includes("merchant")) return "merchant";
    if (text.includes("fundraiser")) return "fundraiser";
    if (text.includes("member")) return "member";
    if (text.includes("user")) return "member";

    return null;
  }, []);

  const getExistingEmailMessage = React.useCallback((type) => {
    if (type === "member") {
      return "This email is already registered as a Member.";
    }

    if (type === "fundraiser") {
      return "This email is already registered as a Fundraiser.";
    }

    if (type === "merchant") {
      return "This email is already registered as a Merchant.";
    }

    return "This email belongs to an existing account and cannot be invited.";
  }, []);

  const getTemplate = (fName, isReinvite = false) => {
    const greeting = `Hi <strong>[Merchant Name]</strong>,`;
    const fundraiser = `<strong>${fName || "Swoodle Fundraiser"}</strong>`;
    const swoodle = `<strong>Swoodle</strong>`;

    // if (isReinvite) {
    //   return `<p>${greeting}</p><p>You were previously invited by ${fundraiser} to join ${swoodle}, the platform that helps local businesses grow while supporting community fundraisers.</p><p>To get started, we just need some basic details about your business. Once your profile is set up, you'll be able to create and manage offers through your <strong>self-service Merchant Dashboard</strong> — connecting your business directly with local supporters.</p>`;
    // }
    return `<p>${greeting}</p><p>${fundraiser} has invited you to join ${swoodle}, where businesses grow by supporting community fundraisers.</p><p>To begin, simply click the button below to share a few details about your business. Once registered, you'll gain access to your <strong>Merchant Dashboard</strong>, where you can easily create offers, track results, and engage local customers.</p>`;
  };

  React.useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const res = await getFundraiserProfile();
        const name = res?.data?.fundraiserName || res?.data?.organisationName || "Swoodle Fundraiser";
        // Only update state — the useEffect on [fundraiserName] will update the template.
        // Do NOT call formik.setFieldValue here — that would trigger a second Quill onChange
        // which incorrectly marks isMessageDirty = true before the user has typed anything.
        setFundraiserName(name);
      } catch (_) {
        setFundraiserName("Swoodle Fundraiser");
      }
    };
    fetchProfileData();
  }, []);

  const checkEmailExistsAsUser = React.useCallback(
    async (emailValue) => {
      if (!emailValue || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
        setEmailExistsAsUser(false);
        setExistingEmailType(null);
        return { exists: false, type: null };
      }

      setIsCheckingEmail(true);
      try {
        const response = await axiosInstance.post("auth/emailVerification", {
          email: emailValue.trim(),
        });
        const result = response?.data;
        const detectedType =
          result?.accountType ||
          result?.role ||
          result?.userType ||
          result?.data?.accountType ||
          result?.data?.role ||
          result?.data?.userType ||
          inferExistingAccountType(result?.message);

        if (result?.success === true && result?.data === true) {
          setEmailExistsAsUser(true);
          setExistingEmailType(detectedType);
          return { exists: true, type: detectedType };
        }

        setEmailExistsAsUser(false);
        setExistingEmailType(null);
        return { exists: false, type: null };
      } catch (error) {
        setEmailExistsAsUser(false);
        setExistingEmailType(null);
        return { exists: false, type: null };
      } finally {
        setIsCheckingEmail(false);
      }
    },
    [inferExistingAccountType],
  );

  const formik = useFormik({
    initialValues: {
      merchantName: "",
      email: "",
      contactPersonNumber: "",
      message: getTemplate("Swoodle Fundraiser"),
    },
    validationSchema: referSchema, // isReinvitation ? reReferSchema : referSchema,
    onSubmit: async (values, { resetForm }) => {
      const emailCheck = await checkEmailExistsAsUser(values.email);
      if (emailCheck?.exists) {
        formik.setFieldError(
          "email",
          getExistingEmailMessage(emailCheck?.type),
        );
        formik.setTouched({ ...formik.touched, email: true }, false);
        return;
      }

      const payload = {
        email: values?.email,
        contactPersonNumber: values?.contactPersonNumber,
      };

      // Restored businessName: Send it if user provides a value
      if (values?.merchantName && values.merchantName.trim() !== "") {
        payload.businessName = values.merchantName;
      }

      // Only send description if the user has manually edited the message beyond the default template
      if (isMessageDirty.current && values.message) {
        let finalMessage = values.message;

        // Remove the [Merchant Name] placeholder so the actual email says "Hi,"
        finalMessage = finalMessage.replace(/ ?<strong>\[Merchant Name\]<\/strong>/g, "");
        finalMessage = finalMessage.replace(/ ?\[Merchant Name\]/g, "");

        // Email clients render paragraphs with inconsistent margins. Convert to <br>-based
        // layout for perfect control.
        // Step 1: Remove empty Quill paragraphs entirely
        finalMessage = finalMessage.replace(/<p><br\s*\/?><\/p>/gi, "");

        // Step 2: Replace </p><p> boundaries with a DOUBLE <br> to create a one-line gap
        finalMessage = finalMessage.replace(/<\/p>\s*<p>/gi, "<br><br>");

        // Step 3: Strip remaining <p> and </p> wrapper tags
        finalMessage = finalMessage.replace(/<\/?p>/gi, "");

        // Step 4: Special fix for greeting spacing - if a line ends with a comma (e.g., "Hi...,")
        // we override the gap to keep it flush (single <br>) with the next line.
        finalMessage = finalMessage.replace(/(,\s*)(<br\s*\/?>\s*){1,}/gi, "$1<br>");

        // Step 5: Collapse any accidental triple breaks back to double
        finalMessage = finalMessage.replace(/(<br\s*\/?>\s*){3,}/gi, "<br><br>");

        payload.description = finalMessage.trim();
      }
      // If not dirty, description is intentionally omitted from payload entirely

      try {
        const referPromise = await dispatch(referMerchant(payload)).unwrap();
        if (referPromise?.success === true) {
          toast.success("Referral sent successfully!");
          setEmailExistsAsUser(false);
          isMessageDirty.current = false;
          quillBaselineRef.current = null;
          resetForm();
          navigate("/fundraiser/referral-list");
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || error?.message || "";
        const detectedType = inferExistingAccountType(
          error?.response?.data?.accountType ||
            error?.response?.data?.role ||
            error?.response?.data?.userType ||
            errorMessage,
        );

        if (
          errorMessage.toLowerCase().includes("already exists") ||
          errorMessage.toLowerCase().includes("already registered") ||
          errorMessage.toLowerCase().includes("user") ||
          errorMessage.toLowerCase().includes("member") ||
          errorMessage.toLowerCase().includes("fundraiser")
        ) {
          formik.setFieldError(
            "email",
            getExistingEmailMessage(detectedType),
          );
          formik.setTouched({ ...formik.touched, email: true }, false);
          setEmailExistsAsUser(true);
          setExistingEmailType(detectedType);
          return;
        }

        toast.error(errorMessage || "Failed to send referral. Please try again.");
      }
    },
  });

  // Sync default template when fundraiser name or invitation type changes
  // We don't sync on merchantName change because the user wants it to stay as a placeholder in the editor
  React.useEffect(() => {
    formik.setFieldValue("message", getTemplate(fundraiserName, isReinvitation));
    // Reset dirty tracking when template changes (new invite type or fundraiser name loaded)
    isMessageDirty.current = false;
    quillBaselineRef.current = null;
  }, [fundraiserName, isReinvitation]);

  const canSubmitReferral =
    !loading &&
    !isCheckingEmail &&
    !emailExistsAsUser &&
    formik.isValid &&
    formik.dirty &&
    Object.keys(formik.errors || {}).length === 0;

  // Check if email already exists in referrals
  // React.useEffect(() => {
  //   const checkEmail = async () => {
  //     const email = formik.values.email?.trim();
  //     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  //     if (!email || !emailRegex.test(email)) {
  //       setIsReinvitation(false);
  //       return;
  //     }

  //     setIsCheckingEmail(true);
  //     try {
  //       // Check referrals first with silent: true to avoid global loader
  //       const res = await dispatch(getFundraiserReferrals({ search: email, limit: 100, silent: true, sort: "-1" })).unwrap();
  //       const foundRef = res?.data?.find(ref =>
  //         (ref.email && ref.email.toLowerCase() === email.toLowerCase()) ||
  //         (ref.user && ref.user.email && ref.user.email.toLowerCase() === email.toLowerCase())
  //       );

  //       if (foundRef) {
  //         setIsReinvitation(true);
  //         // Do NOT auto-populate Business Name or Contact Number.
  //         // These should remain empty so the user can enter them fresh.
  //       } else {
  //         // Fallback: Check if user exists anywhere in system
  //         const authRes = await dispatch(emailVerification({ email: email, silent: true })).unwrap();
  //         setIsReinvitation(authRes?.success === true && authRes?.data === true);
  //       }
  //     } catch (_) {
  //       setIsReinvitation(false);
  //     } finally {
  //       setIsCheckingEmail(false);
  //     }
  //   };

  //   const delayDebounceFn = setTimeout(() => {
  //     checkEmail();
  //   }, 500);

  //   return () => clearTimeout(delayDebounceFn);
  // }, [formik.values.email]);

  const items = [
    {
      title: "Refer a Merchant",
    },
    {
      title: "Refer New Merchant",
    },
  ];

  return (
    <>
      {loading && <Loader />}
      <div className="flex flex-col gap-5">
        <BreadCrumsHeader
          heading="Refer New Merchant"
          items={items}
          form={true}
        />
      </div>

      <div className="flex flex-col lg:flex-row gap-8 mt-5">
        {/* Form Section */}
        <div className="w-full lg:w-[50%]">
          <div className="bg-white rounded-lg shadow-md pt-4">
            <h2 className="text-lg font-semibold mb-4 md:mb-6 px-4 md:px-8">Merchant details</h2>
            <hr />
            <form onSubmit={formik.handleSubmit} className="p-4 md:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 md:gap-y-8">
                <Input
                  label={
                    <span>
                      Business Name <span style={{ color: '#999999', fontWeight: 'normal' }}>(Optional)</span>
                    </span>
                  }
                  placeholder="Enter business name"
                  name="merchantName"
                  value={formik.values.merchantName}
                  handleChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.merchantName}
                  touched={formik.touched.merchantName}
                  width="100%"
                  radius="4px"
                />
                <Input
                  label="Email"
                  placeholder="Enter email address"
                  name="email"
                  value={formik.values.email}
                  handleChange={(e) => {
                    if (emailExistsAsUser) {
                      setEmailExistsAsUser(false);
                    }
                    if (existingEmailType) {
                      setExistingEmailType(null);
                    }
                    formik.handleChange(e);
                  }}
                  handleBlur={async (e) => {
                    formik.handleBlur(e);
                    await checkEmailExistsAsUser(e?.target?.value);
                  }}
                  handleKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                  }}
                  error={
                    emailExistsAsUser
                      ? getExistingEmailMessage(existingEmailType)
                      : formik.errors.email
                  }
                  touched={formik.touched.email || emailExistsAsUser}
                  loading={isCheckingEmail}
                  width="100%"
                  radius="4px"
                />

                {/* Contact Person Number - always visible */}
                <PhoneFormInput
                  label="Contact Person Number"
                  divClassName="w-full"
                  name="contactPersonNumber"
                  value={formik.values.contactPersonNumber}
                  handleBlur={() => formik.setFieldTouched("contactPersonNumber", true)}
                  error={formik.errors.contactPersonNumber}
                  touched={formik.touched.contactPersonNumber}
                  setFieldValue={formik.setFieldValue}
                  containerClass="!rounded-[4px] z-[9999]"
                  inputClass="lg:!h-[45px] md:!h-[45px] !rounded-[4px] !bg-[#FFFFFF] !text-[#303030] lg:!text-[16px] sm:!text-[14px] !font-[400] !outline-none !font-poppins"
                  labelClass="invitationLabels"
                  errorClass={
                    "text-red-600 sm:text-[10px] md:text-[12px] font-[600] ms-1"
                  }
                />
              </div>

              <div className="mt-8">
                <label className="block text-[#303030] text-[14px] font-[600] font-poppins mb-2">Message</label>
                <ReactQuill
                  theme="snow"
                  value={formik.values.message}
                  onChange={(val) => {
                    formik.setFieldValue("message", val);
                    if (quillBaselineRef.current === null) {
                      // First onChange call: Quill is normalising the template on mount.
                      // Capture this as the baseline — NOT a user edit.
                      quillBaselineRef.current = val;
                    } else if (val !== quillBaselineRef.current) {
                      // Any subsequent change that differs from the baseline is a real user edit.
                      isMessageDirty.current = true;
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("message", true)}
                  modules={{ toolbar: [['bold', 'italic', 'underline']] }}
                  className="w-full h-[280px] mb-12"
                />
                {formik.touched.message && formik.errors.message && (
                  <div className="text-red-600 text-xs mt-1">{formik.errors.message}</div>
                )}
              </div>

              <div className="mt-16">
                <button
                  type="submit"
                  disabled={!canSubmitReferral}
                  className={`w-full bg-[#00ADEF] text-white rounded-[8px] py-3 text-[16px] font-[600] font-poppins shadow-md h-[60px] transition-all ${
                    canSubmitReferral
                      ? "hover:bg-[#0096ce]"
                      : "opacity-50 cursor-not-allowed"
                  }`}
                >
                  {loading ? "Sending..." : "Send Referral"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Preview Section */}
        <div className="w-full lg:w-[50%] animate-in fade-in slide-in-from-right duration-500 lg:sticky lg:top-4">
          <div className="w-full flex flex-col items-start gap-4">
            <div className="flex items-center justify-between w-full">
              <h3 className="text-[18px] font-[600] text-[#303030] font-poppins">Email Preview</h3>
              <div className="flex items-center gap-2 bg-[#F5F5F5] rounded-full p-1 border border-[#E0E0E0]">
                <button
                  type="button"
                  onClick={() => setPreviewMode("desktop")}
                  className={`p-2 rounded-full transition-all duration-300 transform ${previewMode === "desktop" ? "bg-[#00ADEF] text-white scale-110 shadow-md" : "text-[#767676] hover:bg-gray-200"}`}
                >
                  <Monitor size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewMode("mobile")}
                  className={`p-2 rounded-full transition-all duration-300 transform ${previewMode === "mobile" ? "bg-[#00ADEF] text-white scale-110 shadow-md" : "text-[#767676] hover:bg-gray-200"}`}
                >
                  <Smartphone size={18} />
                </button>
              </div>
            </div>

            <div className={`w-full transition-all duration-700 ease-in-out flex justify-center ${previewMode === "mobile" ? "py-4" : ""}`}>
              <div
                className="transition-all duration-[800ms] ease-in-out relative flex flex-col overflow-hidden"
                style={{
                  width: previewMode === "mobile" ? "320px" : "100%",
                  height: previewMode === "mobile" ? "660px" : "auto",
                  maxHeight: previewMode === "mobile" ? "660px" : "2000px",
                  borderWidth: previewMode === "mobile" ? "10px" : "1px",
                  borderColor: previewMode === "mobile" ? "#1a1a1b" : "#E0E0E0",
                  borderRadius: previewMode === "mobile" ? "50px" : "24px",
                  padding: previewMode === "mobile" ? "0px" : "16px",
                  backgroundColor: previewMode === "mobile" ? "#FFFFFF" : "#F9F9F9",
                  transitionProperty: "width, height, max-height, border, border-radius, padding, background-color"
                }}
              >
                {/* Dynamic Island for Mobile */}
                <div className={`absolute top-4 left-1/2 -translate-x-1/2 h-7 bg-[#1a1a1b] rounded-full z-20 transition-all duration-500 ease-in-out ${previewMode === "mobile" ? "w-32 opacity-100 scale-100" : "w-0 opacity-0 scale-50"}`}></div>

                <div
                  className={`w-full bg-white flex flex-col font-poppins transition-all duration-[800ms] delay-100 ease-out ${previewMode === "mobile"
                    ? "h-full overflow-y-auto pt-14 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:display-none translate-y-0 opacity-100"
                    : "shadow-sm rounded-[5px] translate-y-4 opacity-100"
                    }`}
                >
                  <div className="p-6 pb-2 flex justify-start">
                    <h2 className={`text-[20px] sm:text-[24px] font-[600] text-[#303030] font-poppins transition-all duration-500 ${previewMode === "mobile" ? "whitespace-nowrap scale-95 origin-left" : ""}`}>Welcome to <span className="text-[#00ADEF]">Swoodle</span></h2>
                  </div>
                  <div className="px-6 pb-2">
                    <h3 className="text-[16px] sm:text-[18px] text-[#303030] font-[500]">
                      {/* {isReinvitation ? (
                        <>Friendly <span className="text-[#00ADEF]">Reminder</span> to Join Swoodle!</>
                      ) : ( */}
                        <>You're Invited to Onboard as a <span className="text-[#00ADEF]">Merchant!</span></>
                      {/* )} */}
                    </h3>
                    <p className="text-[#767676] text-[12px] mt-1">Complete the onboarding process to activate your merchant account.</p>
                  </div>
                  <div className="p-6 pt-0 flex flex-col gap-5">
                    <div className="w-full flex items-center justify-start p-0 mb-2">
                      <img
                        src={
                          /* isReinvitation
                            ? (previewMode === "mobile" ? ReinvitationInfographicMobile : ReinvitationInfographic)
                            : */ (previewMode === "mobile" ? InviteMerchantInfographicMobile : InviteMerchantInfographic)
                        }
                        alt="Swoodle Invitation"
                        className={`${previewMode === "mobile" ? "w-[95%]" : "w-[98%]"} h-auto rounded-[0px] ml-0`}
                      />
                    </div>
                    {/* Message Body */}
                    <div className="text-[#535353] text-[13px] leading-[1.6] text-left whitespace-pre-wrap font-poppins ql-editor px-0" dangerouslySetInnerHTML={{ __html: formik.values.message }}></div>

                    <div className="w-full flex justify-start pl-2">
                      <button type="button" className="w-max px-8 bg-[#00ADEF] text-white py-3 rounded-full font-[600] text-[15px] shadow-sm hover:bg-[#0096d1] transition-all">
                        Get Started
                      </button>
                    </div>

                    <div className="flex flex-col gap-2 text-[12px] text-[#535353] text-left mt-2 pl-2">
                      {/* {!isReinvitation && ( */}
                        <p>You can learn more at: <span className="text-[#00ADEF] underline cursor-pointer">https://swoodle.com.au/merchant-feature/</span></p>
                      {/* )} */}
                      <p className="text-[#303030] font-[500]">
                        {/* {isReinvitation ? "We can't wait to see your first offer go live!" :  */}
                        "We're excited to have you join Swoodle and can't wait to see your first offer go live."
                        {/* } */}
                      </p>
                    </div>

                    <div className="bg-[#F2F4F5] rounded-[24px] p-6 text-left flex flex-col gap-2">
                      <div className="flex flex-col">
                        <span className={`${previewMode === "mobile" ? "text-[15px]" : "text-[16px]"} font-[600] text-[#303030]`}>Warm Regards,</span>
                        <span className={`${previewMode === "mobile" ? "text-[15px]" : "text-[16px]"} font-[600] text-[#303030]`}>The Swoodle Team</span>
                      </div>
                      <p className={`${previewMode === "mobile" ? "text-[12px]" : "text-[13px]"} text-[#535353] border-t border-white/40 pt-1.5`}>
                        {/* {isReinvitation
                          ? "P.S. Every offer you create helps support local causes — that's the power of Swoodle!"
                          : "P.S. Setting up takes just a few minutes — you'll be ready to launch in no time!"} */}
                        P.S. Setting up takes just a few minutes — you'll be ready to launch in no time!
                      </p>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className={`w-full bg-[#242856] text-white p-6 sm:p-8 font-poppins ${previewMode === "mobile" ? "rounded-none" : "rounded-[24px]"}`}>
                    <div className={`flex flex-col ${previewMode === "mobile" ? "items-start text-left" : "md:flex-row justify-between items-start"} gap-4`}>

                      {/* Footers Socials for Mobile - Left Aligned Top */}
                      {previewMode === "mobile" && (
                        <div className="flex flex-col items-start gap-3">
                          <div className="flex gap-2">
                            <FaInstagram size={16} className="text-[#FFC943] hover:opacity-80" />
                            <FaFacebookF size={16} className="text-[#FFC943] hover:opacity-80" />
                            <FaYoutube size={16} className="text-[#FFC943] hover:opacity-80" />
                            <FaLinkedinIn size={16} className="text-[#FFC943] hover:opacity-80" />
                            <FaTiktok size={16} className="text-[#FFC943] hover:opacity-80" />
                          </div>
                          <div className="flex items-center gap-1.5 text-[10px] text-white/60 font-[500]">
                            <span className="hover:text-white cursor-pointer">Contact us</span>
                            <span className="h-2 w-[1px] bg-white/20"></span>
                            <span className="hover:text-white cursor-pointer">Privacy Policy</span>
                            <span className="h-2 w-[1px] bg-white/20"></span>
                            <span className="hover:text-white cursor-pointer">T&C's</span>
                          </div>
                        </div>
                      )}

                      <div className={`flex flex-col ${previewMode === "mobile" ? "items-start" : "items-start"} gap-4`}>
                        <SwoodleLogo className="h-[25px] w-auto" />
                        <p className="text-[11px] text-white/80 leading-relaxed max-w-[280px]">
                          Grow your business, reach new customers. Fundraise with ease, and unlock exclusive deals and savings.
                        </p>
                        <div className={`flex flex-col gap-2 mt-2 ${previewMode === "mobile" ? "items-start" : ""}`}>
                          <div className="flex items-center gap-3 text-[11px]">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="#FFC943"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.21 11.72 11.72 0 003.67 1.17 1 1 0 01.88 1v3.09a1 1 0 01-1 1A17 17 0 013 5a1 1 0 011-1h3.09a1 1 0 011 .88 11.72 11.72 0 001.17 3.67 1 1 0 01-.21 1.11l-2.43 2.13z" /></svg>
                            <span>0401 811 115</span>
                          </div>
                          <div className="flex items-center gap-3 text-[11px]">
                            <img src={EnvelopeIcon} width="16" height="16" alt="email" />
                            <span>info@swoodle.com.au</span>
                          </div>
                          <div className={`flex items-start gap-3 text-[11px] ${previewMode === "mobile" ? "text-left justify-start" : "text-left"}`}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="#FFC943" className="flex-shrink-0 mt-0.5"><path d="M12 2C8.14 2 5 5.14 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.86-3.14-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" /></svg>
                            <div className={`flex text-left ${previewMode === "mobile" ? "flex-col" : "flex-row"}`}>
                              <span>105a Glenferrie Rd Malvern, Victoria, {previewMode !== "mobile" && "3144, Australia"}</span>
                              {previewMode === "mobile" && <span>3144, Australia</span>}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Footer Right side for Desktop only */}
                      {previewMode !== "mobile" && (
                        <div className="flex flex-col items-start md:items-end gap-3 mt-1">
                          <div className="flex gap-1">
                            <FaInstagram size={15} className="text-[#FFC943] hover:opacity-80 cursor-pointer" />
                            <FaFacebookF size={15} className="text-[#FFC943] hover:opacity-80 cursor-pointer" />
                            <FaYoutube size={15} className="text-[#FFC943] hover:opacity-80 cursor-pointer" />
                            <FaLinkedinIn size={15} className="text-[#FFC943] hover:opacity-80 cursor-pointer" />
                            <FaTiktok size={15} className="text-[#FFC943] hover:opacity-80 cursor-pointer" />
                          </div>
                          <div className="flex items-center gap-0.5 text-[10px] text-white/60 font-[500] whitespace-nowrap">
                            <span className="hover:text-white cursor-pointer px-0.5">Contact us</span>
                            <span className="h-3 w-[1px] bg-white/20"></span>
                            <span className="hover:text-white cursor-pointer px-0.5">Privacy Policy</span>
                            <span className="h-3 w-[1px] bg-white/20"></span>
                            <span className="hover:text-white cursor-pointer px-0.5">T&C's</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="w-full h-[1px] bg-white/5 mb-4"></div>
                    <div className="text-[10px] text-center text-white/30">
                      © {new Date().getFullYear()} Swoodle. All rights reserved.
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReferFormPage;
